'use client';

import { Camera, CameraOff } from 'lucide-react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';

interface NavigateResult {
  id: number;
  display: string;
}

interface QrCameraTabProps {
  scannerContainerId: string;
  scannerReady: boolean;
  scannerError: string | null;
  scannedValue: string;
  scannedItems: string[];
  results: NavigateResult[];
  isFetching: boolean;
  searchTriggered: boolean;
  isOnScanValue: boolean;
  onNavigate: (id: number) => void;
  onScanAgain: () => void;
  onStartScanner: () => void;
  t: (key: string) => string;
}

export function QrCameraTabContent({
  scannerContainerId,
  scannerReady,
  scannerError,
  scannedValue,
  scannedItems,
  results,
  isFetching,
  searchTriggered,
  isOnScanValue,
  onNavigate,
  onScanAgain,
  onStartScanner,
  t
}: QrCameraTabProps) {
  return (
    <TabsContent value='camera' className='mt-4 space-y-4'>
      {scannerError ? (
        <div className='bg-muted flex h-64 flex-col items-center justify-center gap-3 rounded-lg'>
          <CameraOff className='text-muted-foreground h-10 w-10' />
          <p className='text-muted-foreground px-4 text-center text-sm'>
            {scannerError}
          </p>
          <Button variant='outline' size='sm' onClick={onStartScanner}>
            {t('tryAgain')}
          </Button>
        </div>
      ) : isOnScanValue ? (
        <>
          <div className='overflow-hidden rounded-lg'>
            <div id={scannerContainerId} className='bg-muted min-h-66' />
            {!scannerReady && !scannerError && (
              <div className='bg-muted flex h-0 items-center justify-center'>
                <p className='text-muted-foreground text-sm'>
                  {t('startingCamera')}
                </p>
              </div>
            )}
          </div>
          {scannedItems.length > 0 && (
            <div className='max-h-37.5 space-y-1 overflow-y-auto'>
              {scannedItems.map((item, idx) => (
                <div
                  key={`${item}-${idx}`}
                  className='bg-muted flex items-center gap-2 rounded px-3 py-2 text-sm'
                >
                  <span className='text-green-600'>&#10003;</span>
                  <span className='font-mono font-medium'>{item}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : scannedValue ? (
        <div className='space-y-3'>
          <div className='bg-muted rounded-lg p-4 text-center'>
            <p className='text-muted-foreground mb-1 text-xs'>
              {t('scannedResult')}
            </p>
            <p className='font-mono text-lg font-semibold'>{scannedValue}</p>
          </div>

          {isFetching && (
            <p className='text-muted-foreground text-center text-sm'>
              {t('searching')}
            </p>
          )}

          {!isFetching && results.length === 0 && searchTriggered && (
            <p className='text-muted-foreground text-center text-sm'>
              {t('noMatch')}
            </p>
          )}

          {results.length > 0 && (
            <div className='space-y-2'>
              {results.map((item) => (
                <button
                  key={item.id}
                  type='button'
                  className='hover:bg-muted w-full rounded-lg border p-3 text-start transition-colors'
                  onClick={() => onNavigate(item.id)}
                >
                  <p className='font-medium'>{item.display}</p>
                </button>
              ))}
            </div>
          )}

          <Button variant='outline' className='w-full' onClick={onScanAgain}>
            <Camera className='me-2 h-4 w-4' />
            {t('scanAgain')}
          </Button>
        </div>
      ) : (
        <div className='overflow-hidden rounded-lg'>
          <div id={scannerContainerId} className='bg-muted min-h-66' />
          {!scannerReady && !scannerError && (
            <div className='bg-muted flex h-0 items-center justify-center'>
              <p className='text-muted-foreground text-sm'>
                {t('startingCamera')}
              </p>
            </div>
          )}
        </div>
      )}
    </TabsContent>
  );
}
